import { PreviewComponent } from '@/services/utils/basePreview'
import { ContentFormatter } from '@/shared/helpers/contentFormatter'
import i18n from '@/plugins/i18n'
import {
  cashOutStatusColors,
  ICashOutOrder
} from '@/modules/order-management/cashout-order/types/cashoutTypes'
import { getCurrencySymbol } from '@/shared/helpers/getCurrencySymbol'

/**
 * dynamic component generator method
 * @param order is IOrderItems interface type which
 * @param isReview to know if that is a review or preview
 * @return An Array of IPreviewComponent order item type
 */
export function cashOutPreviewComponents(
  order: ICashOutOrder,
  isReview = false
): PreviewComponent<ICashOutOrder>[] {
  return [
    PreviewComponent.labelValue<ICashOutOrder>(
      'seller',
      'merchant',
      order.merchant?.fullName
    ).setTitle('cashoutOrderDetails'),
    PreviewComponent.labelValue<ICashOutOrder>(
      'invoiceId',
      'invoiceNumber',
      order.invoiceNumber
    ),
    PreviewComponent.labelValue<ICashOutOrder>(
      'cashoutStatus',
      'status',
      formatText(order.status)
    )
      .setClass(`${cashOutStatusColors[order.status]}--text`)
      .setBreak(true),
    PreviewComponent.table(
      'orderItems',
      [
        {
          text: '',
          value: 'images',
          key: 'images',
          formatValue: 'file',
          io: 'input',
          formatType: 'photo',
          cellProps: item => {
            return {
              src: item?.images[0]?.image,
              height: 40,
              width: 40,
              contain: true
            }
          },
          width: '80px'
        },
        {
          text: i18n.t('header.product'),
          value: 'productName',
          key: 'productName',
          formatValue: 'text'
        },
        {
          text: i18n.t('header.variants'),
          value: (item: any) =>
            item?.productVariantOptions
              ?.map((option: { value: string; name: string }) => option.value)
              ?.join(' / '),
          key: 'productVariantOptions',
          formatValue: 'text'
        },
        {
          text: i18n.t('header.orderedItemId'),
          value: item => `OID-${item?.id}`,
          key: 'id',
          formatValue: 'text'
        },
        {
          text: `${i18n.t('header.unitPrice')} (${getCurrencySymbol()})`,
          value: 'unitPrice',
          key: 'unitPrice',
          formatValue: 'currency'
        },
        {
          text: i18n.t('header.quantity'),
          value: 'quantity',
          key: 'quantity',
          formatValue: 'text'
        },
        {
          text: `${i18n.t('header.totalPrice')} (${getCurrencySymbol()})`,
          value: 'totalAmount',
          key: 'totalAmount',
          formatValue: 'currency'
        }
      ],
      order.orderItems
    )
      .setProp({ class: 'col-12' })
      .setTitle('orderedItems')
      .setBreak(true),
    PreviewComponent.labelValue<ICashOutOrder>(
      'createdBy',
      'createdBy',
      formatText(order.createdBy)
    ),
    PreviewComponent.labelValue<ICashOutOrder>(
      'createdAt',
      'createdAt',
      formatToDate(order.createdAt)
    ),
    PreviewComponent.labelValue<ICashOutOrder>(
      'updatedBy',
      'updatedBy',
      formatText(order.updatedBy)
    ),
    PreviewComponent.labelValue<ICashOutOrder>(
      'updatedAt',
      'updatedAt',
      formatToDate(order.updatedAt)
    ),
    PreviewComponent.labelValue<ICashOutOrder>(
      'reviewedBy',
      'reviewedBy',
      formatText(order.reviewedBy)
    ).setHidden(isReview),
    PreviewComponent.labelValue<ICashOutOrder>(
      'reviewedAt',
      'reviewedAt',
      formatToDate(order.reviewedAt)
    ).setHidden(isReview)
  ]
}

function formatToDate(item: string | Date): string | undefined {
  return ContentFormatter.$formatToDateTime(item)
}

function formatText(item: string): string | undefined {
  return ContentFormatter.$customizeText(item)
}
